import { JSX, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { read_cookie } from 'sfcookies';
import { ChatConversion } from '../context/ChatBotContextProvider';
import { CapturedStamp, isReplayed } from './CapturedStamp';
import { hostUrl } from '../../utils/url-manager';
import { permissionManager, Permissions } from '../../permissions/manager';
import { accountStatus, accountTypes } from '../../../defaults/defaults';

/** The two verbs a conversion needs. Both, because it creates two records. */
export const CONVERSION_RESOURCES: string[] = [
    'HQ_Customer::customers.create',
    'HQ_Customer::accounts.create',
];

/** What the endpoint answers with on success. */
interface ConversionResult {
    customerId: number;
    customerAccountId: number;
    temporaryPasswordEmailed: boolean;
    sourceClosed: boolean;
    /** The react-admin route of the new account, ready to go into the hash. */
    accountPath: string;
}

interface ConversionCardProps {
    conversion: ChatConversion;
    /** From `usePermissions()` above. The endpoint refuses regardless; this tells them early. */
    permissions?: Permissions;
    capturedAt?: number;
}

/**
 * An offer to turn a sales contact into a customer, with everything it would create.
 *
 * **The assistant does not do this; this card does, when the admin presses Create.** The request
 * goes to HQ-API on the ordinary `hq` firewall carrying the admin's own token, the same way the
 * "Add domain" button on the account screen does, so the ACL applies exactly as it does to any HQ
 * screen and nothing asserts an identity across a service boundary. Daive only ever sent the frame
 * that drew this.
 *
 * **The fields are editable on purpose.** A contact form holds whatever somebody typed into a
 * public form, so a surname in the first name box is ordinary, and correcting it here is the
 * difference between an approval and a rubber stamp. What is sent is what is on screen.
 *
 * Three things not to undo:
 *
 * - **The button leaves on the first press and does not come back.** A second press would be a
 *   second customer. HQ-API refuses the duplicate email, so this is the courtesy and that is the
 *   guard, but a card that can be pressed twice will be.
 * - **A replayed card has no buttons at all.** HQ-11: a resumed conversation can be a fortnight
 *   old, and the enquiry behind it may have been converted by somebody else since.
 * - **The redirect on success is deliberate**, and it is not the same case as `OpenInHqButton`,
 *   which offers rather than moves. Moving somebody while they are reading is hostile; moving them
 *   to the thing they have just pressed a button to create is answering the click.
 */
export const ConversionCard: (props: ConversionCardProps) => JSX.Element =
    ({ conversion, permissions, capturedAt }): JSX.Element => {
        const [email, setEmail] = useState<string>(conversion.customer.email);
        const [firstName, setFirstName] = useState<string>(conversion.customer.firstName);
        const [lastName, setLastName] = useState<string>(conversion.customer.lastName);
        const [telephone, setTelephone] = useState<string>(conversion.customer.telephone);
        const [jobTitle, setJobTitle] = useState<string>(conversion.customer.jobTitle);
        const [type, setType] = useState<string>(conversion.account.type);
        const [status, setStatus] = useState<string>(conversion.account.status);

        const [outcome, setOutcome] = useState<'open' | 'creating' | 'created' | 'refused'>('open');
        const [message, setMessage] = useState<string>('');

        const replayed: boolean = isReplayed(capturedAt);
        const missing: string[] = CONVERSION_RESOURCES.filter(
            (resource: string): boolean => !permissionManager.isAllowed(permissions, resource)
        );

        function open(path: string): void {
            // Same mechanism as OpenInHqButton for a same-origin record: HQ is hash routed, so
            // setting the hash moves the admin in place rather than reloading the application.
            window.location.hash = '#/' + path;
        }

        async function create(): Promise<void> {
            setOutcome('creating');
            setMessage('');

            try {
                const response: Response = await fetch(hostUrl + 'hq/customer/conversion', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'token': read_cookie('auth') as unknown as string,
                    },
                    body: JSON.stringify({
                        sourceResource: conversion.sourceResource,
                        sourceId: conversion.sourceId,
                        customer: {
                            email: email,
                            firstName: firstName,
                            lastName: lastName,
                            telephone: telephone,
                            jobTitle: jobTitle,
                        },
                        account: { type: type, status: status },
                    }),
                });
                const body: { status?: string, message?: string, data?: ConversionResult } =
                    await response.json();

                if (body?.status !== 'success' || !body?.data) {
                    // The endpoint's own wording. It names the customer already using that email,
                    // or the setting that stopped the password email, so it is shown as it came
                    // rather than reworded here.
                    setOutcome('refused');
                    setMessage(body?.message ?? 'That customer could not be created.');
                    return;
                }

                setOutcome('created');
                setMessage(body.message ?? '');
                open(body.data.accountPath);
            } catch (error) {
                setOutcome('refused');
                setMessage('That customer could not be created.');
                console.error(error);
            }
        }

        return (
            <div className={ 'chat__conversion is-' + outcome }>
                <p className="conversion__heading">Convert this contact into a customer</p>

                { conversion.sourceLabel !== '' && (
                    <p className="conversion__source">
                        From { conversion.sourceResource.replace(/_/g, ' ') } { conversion.sourceId },
                        { ' ' }{ conversion.sourceLabel }
                    </p>
                ) }
                { conversion.why !== '' && <p className="conversion__why">{ conversion.why }</p> }

                <div className="conversion__fields">
                    <label>
                        <span>Email</span>
                        <input
                            type="email"
                            value={ email }
                            disabled={ outcome !== 'open' || replayed }
                            onChange={ (event): void => setEmail(event.target.value) }
                        />
                    </label>
                    <label>
                        <span>First name</span>
                        <input
                            type="text"
                            value={ firstName }
                            disabled={ outcome !== 'open' || replayed }
                            onChange={ (event): void => setFirstName(event.target.value) }
                        />
                    </label>
                    <label>
                        <span>Last name</span>
                        <input
                            type="text"
                            value={ lastName }
                            disabled={ outcome !== 'open' || replayed }
                            onChange={ (event): void => setLastName(event.target.value) }
                        />
                    </label>
                    <label>
                        <span>Telephone</span>
                        <input
                            type="text"
                            value={ telephone }
                            disabled={ outcome !== 'open' || replayed }
                            onChange={ (event): void => setTelephone(event.target.value) }
                        />
                    </label>
                    <label>
                        <span>Job title</span>
                        <input
                            type="text"
                            value={ jobTitle }
                            disabled={ outcome !== 'open' || replayed }
                            onChange={ (event): void => setJobTitle(event.target.value) }
                        />
                    </label>
                    <label>
                        <span>Account type</span>
                        <select
                            value={ type }
                            disabled={ outcome !== 'open' || replayed }
                            onChange={ (event): void => setType(event.target.value) }
                        >
                            { accountTypes.map((choice): JSX.Element => (
                                <option key={ String(choice.id) } value={ String(choice.id) }>
                                    { choice.name }
                                </option>
                            )) }
                        </select>
                    </label>
                    <label>
                        <span>Account status</span>
                        <select
                            value={ status }
                            disabled={ outcome !== 'open' || replayed }
                            onChange={ (event): void => setStatus(event.target.value) }
                        >
                            { accountStatus.map((choice): JSX.Element => (
                                <option key={ String(choice.id) } value={ String(choice.id) }>
                                    { choice.name }
                                </option>
                            )) }
                        </select>
                    </label>
                </div>

                <p className="conversion__note">
                    Creating this sends a temporary password to { email || 'that address' }.
                </p>

                { /* HQ-11. A replayed card is a record of an offer, not an offer. The enquiry may
                     have been converted by somebody else since, so it says when it was shown and
                     has no buttons at all. */ }
                { replayed && (
                    <p className="conversion__replayed">
                        <CapturedStamp capturedAt={ capturedAt as number } />
                    </p>
                ) }

                { !replayed && missing.length > 0 && (
                    <p className="conversion__outcome conversion__outcome--denied">
                        Your role cannot create { missing.includes(CONVERSION_RESOURCES[0])
                            ? 'customers' : 'customer accounts' }, so this cannot be approved here.
                    </p>
                ) }

                { !replayed && missing.length === 0 && outcome === 'open' && (
                    <div className="conversion__actions">
                        <button type="button" className="conversion__create" onClick={ create }>
                            <CheckIcon fontSize="small" />
                            Create customer
                        </button>
                        <button
                            type="button"
                            className="conversion__dismiss"
                            onClick={ (): void => { setOutcome('refused'); setMessage('Left as it was.'); } }
                        >
                            <CloseIcon fontSize="small" />
                            No thanks
                        </button>
                    </div>
                ) }

                { outcome === 'creating' && (
                    <p className="conversion__outcome">Creating the customer and their account.</p>
                ) }
                { outcome === 'created' && (
                    <p className="conversion__outcome conversion__outcome--done">{ message }</p>
                ) }
                { outcome === 'refused' && message !== '' && (
                    <p className="conversion__outcome conversion__outcome--refused">{ message }</p>
                ) }
            </div>
        );
    };
